import { CloseIcon } from '@blocksuite/icons/rc';
import type {
  DialogContentProps,
  DialogOverlayProps,
  DialogPortalProps,
  DialogProps,
} from '@radix-ui/react-dialog';
import * as Dialog from '@radix-ui/react-dialog';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import { assignInlineVars } from '@vanilla-extract/dynamic';
import clsx from 'clsx';
import type { CSSProperties } from 'react';
import {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { startScopedViewTransition } from '../../utils';
import type { IconButtonProps } from '../button';
import { IconButton } from '../button';
import { SafeArea } from '../safe-area';
import { InsideModalContext, ModalConfigContext } from './context';
import * as styles from './styles.css';

export interface ModalProps extends DialogProps {
  width?: CSSProperties['width'];
  height?: CSSProperties['height'];
  minHeight?: CSSProperties['minHeight'];
  title?: React.ReactNode;
  headerClassName?: string;
  description?: React.ReactNode;
  descriptionClassName?: string;
  withoutCloseButton?: boolean;
  /**
   * __Click outside__ or __Press `Esc`__ won't close the modal
   * @default false
   */
  persistent?: boolean;
  portalOptions?: DialogPortalProps;
  contentOptions?: DialogContentProps;
  overlayOptions?: DialogOverlayProps;
  closeButtonOptions?: IconButtonProps;
  contentWrapperClassName?: string;
  contentWrapperStyle?: CSSProperties;
  /**
   * @default 'fadeScaleTop'
   */
  animation?: 'fadeScaleTop' | 'none' | 'slideBottom' | 'slideRight';
  /**
   * Whether to show the modal in full screen mode
   */
  fullScreen?: boolean;
  disableAutoFocus?: boolean;
}
type PointerDownOutsideEvent = Parameters<
  Exclude<DialogContentProps['onPointerDownOutside'], undefined>
>[0];

const getVar = (style: number | string = '', defaultValue = '') => {
  return style
    ? typeof style === 'number'
      ? `${style}px`
      : style
    : defaultValue;
};

/**
 * This component is a hack to support `startViewTransition` in the modal.
 */
class ModalTransitionContainer extends HTMLElement {
  pendingTransitionNodes: Node[] = [];
  animationFrame: number | null = null;

  /**
   * This method will be called when the modal is removed from the DOM
   * https://github.com/facebook/react/blob/e4b4aac2a01b53f8151ca85148873096368a7de2/packages/react-dom-bindings/src/client/ReactFiberConfigDOM.js#L833
   */
  override removeChild<T extends Node>(child: T): T {
    if (typeof document.startViewTransition === 'function') {
      // React has already emptied the portals inside; the closing picture
      // needs their content back.
      if (child instanceof Element) {
        child
          .querySelectorAll<ModalPortalSlot>(MODAL_PORTAL_SLOT_TAG)
          .forEach(slot => slot.restoreDetached());
      }
      this.pendingTransitionNodes.push(child);
      this.requestTransition();
      return child;
    } else {
      // oxlint-disable-next-line unicorn/prefer-dom-node-remove
      return super.removeChild(child);
    }
  }

  /**
   * We collect all the nodes that are removed in the single frame and then trigger the transition.
   */
  private requestTransition() {
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
    }

    this.animationFrame = requestAnimationFrame(() => {
      const nodes = this.pendingTransitionNodes;
      nodes.forEach(child => {
        if (child instanceof HTMLElement) {
          child.classList.add('vt-active');
        }
      });
      startScopedViewTransition(styles.modalVTScope, () => {
        nodes.forEach(child => {
          // oxlint-disable-next-line unicorn/prefer-dom-node-remove
          super.removeChild(child);
        });
      });
      this.pendingTransitionNodes = [];
    });
  }
}

const MODAL_PORTAL_SLOT_TAG = 'modal-portal-slot';

/**
 * Target of a portal inside a modal. React removes the content of a portal
 * before the modal itself; the slot remembers it until the end of the commit
 * so a closing modal can put it back into its leaving picture.
 */
class ModalPortalSlot extends HTMLElement {
  private detached: Node[] = [];

  connectedCallback() {
    // The slot adds no box; its content lays out as a child of the host.
    // Set on connect: an element that gives itself attributes while it is
    // created is rejected by the browser and replaced with a plain one.
    this.style.display = 'contents';
  }

  override removeChild<T extends Node>(child: T): T {
    // oxlint-disable-next-line unicorn/prefer-dom-node-remove
    super.removeChild(child);
    if (this.detached.length === 0) {
      queueMicrotask(() => {
        this.detached = [];
      });
    }
    this.detached.push(child);
    return child;
  }

  /** Puts back what this commit removed; the whole modal leaves with it. */
  restoreDetached() {
    this.append(...this.detached);
    this.detached = [];
  }
}

let defined = false;
function defineModalElements() {
  if (defined) return;
  customElements.define('modal-transition-container', ModalTransitionContainer);
  customElements.define(MODAL_PORTAL_SLOT_TAG, ModalPortalSlot);
  defined = true;
}

function createContainer() {
  defineModalElements();
  const container = document.createElement(
    'modal-transition-container'
  ) as ModalTransitionContainer;
  document.body.append(container);
  return container;
}

/**
 * A portal target inside a modal whose content stays visible while the modal
 * closes. Pass `host` as ref of the element the portal content belongs in and
 * render the portal into `slot`.
 */
export function useModalPortalSlot() {
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  const host = useCallback((element: HTMLElement | null) => {
    if (!element) return;
    defineModalElements();
    const next = document.createElement(
      MODAL_PORTAL_SLOT_TAG
    ) as ModalPortalSlot;
    element.append(next);
    setSlot(next);
  }, []);
  return { host, slot };
}

export const ModalInner = forwardRef<HTMLDivElement, ModalProps>(
  (props, ref) => {
    const { onOpen: modalConfigOnOpen, dynamicKeyboardHeight } =
      useContext(ModalConfigContext);
    const {
      modal,
      portalOptions,
      open,
      onOpenChange,
      width,
      height,
      minHeight = 194,
      title,
      headerClassName,
      description,
      descriptionClassName,
      withoutCloseButton = false,
      persistent,
      contentOptions: {
        style: contentStyle,
        className: contentClassName,
        onPointerDownOutside,
        onEscapeKeyDown,
        onCloseAutoFocus,
        ...otherContentOptions
      } = {},
      overlayOptions: {
        className: overlayClassName,
        style: overlayStyle,
        ...otherOverlayOptions
      } = {},
      closeButtonOptions,
      children,
      contentWrapperClassName,
      contentWrapperStyle,
      animation = BUILD_CONFIG.isMobileEdition ? 'slideBottom' : 'fadeScaleTop',
      fullScreen,
      disableAutoFocus,
      ...otherProps
    } = props;
    const { className: closeButtonClassName, ...otherCloseButtonProps } =
      closeButtonOptions || {};

    const [container, setContainer] = useState<ModalTransitionContainer | null>(
      null
    );

    useEffect(() => {
      if (open) return modalConfigOnOpen?.();
      return;
    }, [modalConfigOnOpen, open]);

    // Radix hands focus back on close only to a Dialog.Trigger. Our modals
    // are opened by state and have none, so remember what had focus when the
    // modal opened and return there (WAI-ARIA dialog pattern).
    const returnFocusRef = useRef<HTMLElement | null>(null);
    useEffect(() => {
      if (!open) return;
      returnFocusRef.current =
        document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null;
    }, [open]);

    useEffect(() => {
      if (open) {
        const container = createContainer();
        setContainer(container);
        return () => {
          setTimeout(() => {
            container.remove();
          }, 1000) as unknown as number;
        };
      } else {
        setContainer(null);
        return;
      }
    }, [open]);

    const handlePointerDownOutSide = useCallback(
      (e: PointerDownOutsideEvent) => {
        onPointerDownOutside?.(e);
        persistent && e.preventDefault();
      },
      [onPointerDownOutside, persistent]
    );

    const handleEscapeKeyDown = useCallback(
      (e: KeyboardEvent) => {
        onEscapeKeyDown?.(e);
        persistent && e.preventDefault();
      },
      [onEscapeKeyDown, persistent]
    );

    const handleAutoFocus = useCallback(
      (e: Event) => {
        disableAutoFocus && e.preventDefault();
      },
      [disableAutoFocus]
    );

    const handleCloseAutoFocus = useCallback(
      (e: Event) => {
        onCloseAutoFocus?.(e);
        if (e.defaultPrevented) return;
        returnFocusRef.current?.focus();
      },
      [onCloseAutoFocus]
    );

    if (!container) {
      return;
    }

    const content = (
      <SafeArea
        bottom={BUILD_CONFIG.isMobileEdition}
        bottomOffset={dynamicKeyboardHeight ?? 12}
        data-full-screen={fullScreen}
        data-modal={modal}
        className={clsx(
          `anim-${animation}`,
          styles.modalContentWrapper,
          contentWrapperClassName
        )}
        data-mobile={BUILD_CONFIG.isMobileEdition ? '' : undefined}
        style={contentWrapperStyle}
      >
        <Dialog.Content
          onPointerDownOutside={handlePointerDownOutSide}
          onEscapeKeyDown={handleEscapeKeyDown}
          className={clsx(styles.modalContent, contentClassName)}
          onOpenAutoFocus={handleAutoFocus}
          onCloseAutoFocus={handleCloseAutoFocus}
          style={{
            ...assignInlineVars({
              [styles.widthVar]: getVar(width, fullScreen ? '100dvw' : '50dvw'),
              [styles.heightVar]: getVar(
                height,
                fullScreen ? '100dvh' : 'unset'
              ),
              [styles.minHeightVar]: getVar(minHeight, '26px'),
            }),
            ...contentStyle,
          }}
          {...(description ? {} : { 'aria-describedby': undefined })}
          {...otherContentOptions}
          ref={ref}
        >
          {withoutCloseButton ? null : (
            <Dialog.Close asChild>
              <IconButton
                size="20"
                className={clsx(styles.closeButton, closeButtonClassName)}
                aria-label="Close"
                data-testid="modal-close-button"
                {...otherCloseButtonProps}
              >
                <CloseIcon />
              </IconButton>
            </Dialog.Close>
          )}
          {title ? (
            <Dialog.Title className={clsx(styles.modalHeader, headerClassName)}>
              {title}
            </Dialog.Title>
          ) : (
            // Refer: https://www.radix-ui.com/primitives/docs/components/dialog#title
            // If you want to hide the title, wrap it inside our Visually Hidden utility like this <VisuallyHidden asChild>.
            <VisuallyHidden.Root asChild>
              <Dialog.Title></Dialog.Title>
            </VisuallyHidden.Root>
          )}
          {description ? (
            <Dialog.Description asChild>
              <div
                className={clsx(styles.modalDescription, descriptionClassName)}
              >
                {description}
              </div>
            </Dialog.Description>
          ) : null}

          {children}
        </Dialog.Content>
      </SafeArea>
    );

    return (
      <Dialog.Root
        modal={modal}
        open={open}
        onOpenChange={onOpenChange}
        {...otherProps}
      >
        <Dialog.Portal container={container} {...portalOptions}>
          {/* Radix renders no overlay for a non-modal dialog: its content
              stands without backdrop there, and the page keeps taking input. */}
          {modal === false ? (
            content
          ) : (
            <Dialog.Overlay
              className={clsx(
                `anim-${animation}`,
                styles.modalOverlay,
                overlayClassName,
                { mobile: BUILD_CONFIG.isMobileEdition }
              )}
              style={{
                ...overlayStyle,
              }}
              {...otherOverlayOptions}
            >
              {content}
            </Dialog.Overlay>
          )}
        </Dialog.Portal>
      </Dialog.Root>
    );
  }
);

ModalInner.displayName = 'ModalInner';

export const Modal = forwardRef<HTMLDivElement, ModalProps>((props, ref) => {
  const insideModal = useContext(InsideModalContext);
  if (!props.open) {
    return;
  }
  return (
    <InsideModalContext.Provider value={insideModal + 1}>
      <ModalInner {...props} ref={ref} />
    </InsideModalContext.Provider>
  );
});

Modal.displayName = 'Modal';

export const useIsInsideModal = () => {
  const context = useContext(InsideModalContext);
  return context > 0;
};
